import React, { useState, useEffect } from 'react';
import { 
    Dialog, DialogTitle, DialogContent, DialogActions, 
    Button, Box, Typography, IconButton, Grid, 
    TextField, MenuItem, Stack, Paper, Divider 
} from '@mui/material';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faTrashAlt, faCheckCircle, faMoneyBillWave } from "@fortawesome/free-solid-svg-icons";

export default function PosPaymentModal({ 
    open, onClose, total, paymentMethods = [], onCompleteOrder,
    appliedPayments, setAppliedPayments, countryId, exchangeRate 
}) {
    const [selectedMethodId, setSelectedMethodId] = useState('');
    const [selectedAccountId, setSelectedAccountId] = useState('');
    const [amountToPay, setAmountToPay] = useState('');
    const [reference, setReference] = useState('');

    // Cálculos principales
    const totalPaid = appliedPayments.reduce((acc, curr) => acc + parseFloat(curr.amount), 0);
    const remaining = Math.max(0, total - totalPaid);
    const change = Math.max(0, totalPaid - total);
    const isFullyPaid = totalPaid >= total;

    // Al abrir el modal, sugerimos el monto restante (sin borrar los pagos previos)
    useEffect(() => {
        if (open) {
            resetForm(remaining);
        }
    }, [open, remaining]);

    const resetForm = (suggestedAmount) => {
        setSelectedMethodId('');
        setSelectedAccountId('');
        setReference('');
        setAmountToPay(suggestedAmount > 0 ? suggestedAmount.toFixed(2) : '');
    };

    const handleMethodSelect = (methodId) => {
        setSelectedMethodId(methodId);
        const method = paymentMethods.find(m => m.id === methodId);
        if (method && method.accounts.length === 1) {
            setSelectedAccountId(method.accounts[0].id);
        } else {
            setSelectedAccountId('');
        }
    };

    const handleAddPayment = () => {
        const amount = parseFloat(amountToPay);
        if (!selectedMethodId || isNaN(amount) || amount <= 0) return;

        const method = paymentMethods.find(m => m.id === selectedMethodId);
        const account = method.accounts.find(a => a.id === selectedAccountId);

        if (method.accounts.length > 0 && !selectedAccountId) {
            alert("Por favor seleccione una cuenta receptora.");
            return;
        }

        const newPayment = {
            id: Date.now(),
            method_id: method.id,
            method_name: method.name,
            account_id: account ? account.id : null,
            account_details: account ? `${account.bank_name || ''} - ${account.account_number || account.email}` : null,
            amount: amount,
            reference: reference
        };

        setAppliedPayments([...appliedPayments, newPayment]);
        resetForm(Math.max(0, remaining - amount));
    };

    const removePayment = (idToRemove) => {
        setAppliedPayments(appliedPayments.filter(p => p.id !== idToRemove));
    };

    const handleProcessOrder = () => {
        /*if (isFullyPaid) {
            onCompleteOrder(appliedPayments, change);
        }*/
       onCompleteOrder(appliedPayments, change);
    };

    const activeMethod = paymentMethods.find(m => m.id === selectedMethodId);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2, borderBottom: '1px solid #e2e8f0' }}>
                <Typography variant="h6" fontWeight="800" color="#0F172A">
                    <FontAwesomeIcon icon={faMoneyBillWave} style={{ marginRight: '10px', color: '#0ea5e9' }} />
                    Payment Process
                </Typography>
                <IconButton onClick={onClose} size="small"><FontAwesomeIcon icon={faClose}/></IconButton>
            </DialogTitle>
            
            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: '400px' }}>
                
                {/* --- COLUMNA IZQUIERDA: Formulario de Pago --- */}
                <Box sx={{ flex: 1, p: 3, bgcolor: 'white' }}>
                    <Typography variant="subtitle2" fontWeight="700" color="text.secondary" mb={2}>
                        SELECT PAYMENT METHOD
                    </Typography>
                    
                    {paymentMethods.length === 0 ? (
                        <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#fef2f2', borderRadius: 2, border: '1px dashed #f87171' }}>
                            <Typography variant="body2" color="#b91c1c" fontWeight="600">No payment methods available.</Typography>
                        </Box>
                    ) : (
                        <Grid container spacing={1.5} mb={3}>
                            {paymentMethods.map(method => (
                                <Grid item xs={6} sm={4} key={method.id}>
                                    <Paper
                                        elevation={0}
                                        onClick={() => handleMethodSelect(method.id)}
                                        sx={{
                                            p: 1.5, textAlign: 'center', borderRadius: 2, border: '1px solid', cursor: 'pointer',
                                            borderColor: selectedMethodId === method.id ? '#0ea5e9' : '#e2e8f0',
                                            bgcolor: selectedMethodId === method.id ? '#f0f9ff' : 'transparent',
                                            '&:hover': { borderColor: '#0ea5e9' }
                                        }}
                                    >
                                        <Typography variant="body2" fontWeight="700" color={selectedMethodId === method.id ? '#0369a1' : '#0F172A'}>
                                            {method.name}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    )}

                    {activeMethod && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, animation: 'fadeIn 0.3s' }}>
                            {activeMethod.accounts.length > 0 && (
                                <TextField
                                    select fullWidth label="Receiving Account" variant="outlined" size="small"
                                    value={selectedAccountId} onChange={(e) => setSelectedAccountId(e.target.value)}
                                >
                                    {activeMethod.accounts.map(acc => (
                                        <MenuItem key={acc.id} value={acc.id}>
                                            {acc.bank_name} {acc.account_number ? `- *${acc.account_number.slice(-4)}` : ''}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )}
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                    fullWidth label="Amount" type="number" variant="outlined" size="small"
                                    inputProps={{ step: 'any', min: '0' }}
                                    value={amountToPay} onChange={(e) => setAmountToPay(e.target.value)}
                                />
                                {activeMethod.accounts.length > 0 && (
                                    <TextField
                                        fullWidth label="Reference (Optional)" variant="outlined" size="small"
                                        value={reference} onChange={(e) => setReference(e.target.value)}
                                    />
                                )}
                            </Box>
                            <Button 
                                variant="contained" onClick={handleAddPayment}
                                disabled={!amountToPay || amountToPay <= 0 || (activeMethod.accounts.length > 0 && !selectedAccountId)}
                                sx={{ py: 1, borderRadius: 2, fontWeight: '700', bgcolor: '#0F172A' }}
                            >
                                Registrar Pago
                            </Button>
                        </Box>
                    )}
                </Box>

                {/* --- COLUMNA DERECHA: Resumen y Líneas de Pago --- */}
                <Box sx={{ width: { xs: '100%', md: '340px' }, p: 3, bgcolor: '#f8fafc', borderLeft: { md: '1px solid #e2e8f0' } }}>
                    
                    {/* Tarjeta de Totales con conversión a Bs. */}
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: isFullyPaid ? '#dcfce7' : '#0F172A', color: isFullyPaid ? '#166534' : 'white', mb: 3 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                            <Typography variant="body2" opacity={0.8} pt={0.5}>Total</Typography>
                            <Box textAlign="right">
                                <Typography variant="body1" fontWeight="700">${total.toFixed(2)}</Typography>
                                {countryId === 1 && (
                                    <Typography variant="caption" sx={{ color: isFullyPaid ? '#15803d' : '#4ade80' }}>
                                        Bs. {(total * exchangeRate).toFixed(2)}
                                    </Typography>
                                )}
                            </Box>
                        </Stack>
                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', my: 1 }} />
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
                            <Typography variant="subtitle2" opacity={0.8} pb={0.5}>
                                {isFullyPaid ? 'Vuelto' : 'Restante'}
                            </Typography>
                            <Box textAlign="right">
                                <Typography variant="h4" fontWeight="800">
                                    ${isFullyPaid ? change.toFixed(2) : remaining.toFixed(2)}
                                </Typography>
                                {countryId === 1 && (
                                    <Typography variant="body2" fontWeight="600" sx={{ color: isFullyPaid ? '#15803d' : '#4ade80' }}>
                                        Bs. {(isFullyPaid ? (change * exchangeRate) : (remaining * exchangeRate)).toFixed(2)}
                                    </Typography>
                                )}
                            </Box>
                        </Stack>
                    </Paper>

                    <Typography variant="caption" fontWeight="700" color="text.secondary" mb={1} display="block">APPLIED PAYMENTS</Typography>
                    {appliedPayments.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">No payments applied yet.</Typography>
                    ) : (
                        <Stack spacing={1}>
                            {appliedPayments.map(p => (
                                <Box key={p.id} sx={{ p: 1.5, bgcolor: 'white', borderRadius: 2, border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight="700" color="#0F172A">
                                            {p.method_name} <Typography component="span" color="#0ea5e9" fontWeight="800">${p.amount.toFixed(2)}</Typography>
                                        </Typography>
                                        {countryId === 1 && (
                                            <Typography variant="caption" color="text.secondary" display="block">Bs. {(p.amount * exchangeRate).toFixed(2)}</Typography>
                                        )}
                                        {p.reference && <Typography variant="caption" color="text.secondary" display="block">Ref: {p.reference}</Typography>}
                                    </Box>
                                    <IconButton size="small" color="error" onClick={() => removePayment(p.id)}><FontAwesomeIcon icon={faTrashAlt} size="xs"/></IconButton>
                                </Box>
                            ))}
                        </Stack>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, bgcolor: 'white', borderTop: '1px solid #e2e8f0' }}>
                <Button onClick={onClose} sx={{ color: '#64748b', fontWeight: '600' }}>Cancelar</Button>
                <Button 
                    variant="contained"
                    onClick={handleProcessOrder} 
                    //disabled={!isFullyPaid}
                    sx={{ 
                        px: 4, 
                        py: 1, 
                        borderRadius: 2, 
                        fontWeight: '700', 
                        //bgcolor: isFullyPaid ? '#10b981' : '#cbd5e1', '&:hover': { bgcolor: isFullyPaid ? '#059669' : '#cbd5e1' } 
                        bgcolor: '#10b981' 
                    }}
                >
                    <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '8px' }} /> Registrar Orden
                </Button>
            </DialogActions>
        </Dialog>
    );
}